import { faChevronDown, faChevronRight, faChevronUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApplicationContext } from "components/Application";
import { useFiltersContext } from "components/FiltersContext";
import { capitalize, uniqBy, without } from "lodash";
import React, { Fragment, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import "twin.macro";

type ListFiltersProps = {
  brand: any;
  season: any;
  line?: any;
  colorGroups: any;
  compositionGroups: any;
  kindGroups: any;
};

export const ListFilters: React.FC<ListFiltersProps> = (props) => {
  const { brand, season, line, colorGroups, compositionGroups, kindGroups } = props;
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();
  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const {
    filteredColorGroups,
    setFilteredColorGroups,
    filteredCompositionGroups,
    setFilteredCompositionGroups,
    filteredKindGroups,
    setFilteredKindGroups,
  } = useFiltersContext();

  const [collapsed, setCollapsed] = useState(true);

  const addColorGroups = useCallback(
    (colorGroup: any) => {
      setFilteredColorGroups((filteredColorGroups) => uniqBy([...filteredColorGroups, colorGroup], "id"));
    },
    [setFilteredColorGroups],
  );

  const delColorGroups = useCallback(
    (colorGroup: any) => {
      setFilteredColorGroups((filteredColorGroups) => without(filteredColorGroups, colorGroup));
    },
    [setFilteredColorGroups],
  );

  const addCompositionGroups = useCallback(
    (compositionGroup: any) => {
      setFilteredCompositionGroups((filteredCompositionGroups) =>
        uniqBy([...filteredCompositionGroups, compositionGroup], "id"),
      );
    },
    [setFilteredCompositionGroups],
  );

  const delCompositionGroups = useCallback(
    (compositionGroup: any) => {
      setFilteredCompositionGroups((filteredCompositionGroups) => without(filteredCompositionGroups, compositionGroup));
    },
    [setFilteredCompositionGroups],
  );

  const addKindGroups = useCallback(
    (kindGroup: any) => {
      setFilteredKindGroups((filteredKindGroups) => uniqBy([...filteredKindGroups, kindGroup], "id"));
    },
    [setFilteredKindGroups],
  );

  const delKindGroups = useCallback(
    (kindGroup: any) => {
      setFilteredKindGroups((filteredKindGroups) => without(filteredKindGroups, kindGroup));
    },
    [setFilteredKindGroups],
  );

  return (
    <div>
      <div tw="flex w-full justify-between px-6">
        <div tw="text-gray-500">
          <Link to="/" tw="hover:(text-gray-900)">
            {brand[`name${languageSuffix}`].toUpperCase()}
          </Link>
          <FontAwesomeIcon icon={faChevronRight} tw="mx-2" />
          <Link to={`/b/${brand.id}/s/${season.id}`} tw="hover:(text-gray-900)">
            {`${season[`name${languageSuffix}`]} ${season[`kind${languageSuffix}`]}`.toUpperCase()}
          </Link>
          {line && (
            <Fragment>
              <FontAwesomeIcon icon={faChevronRight} tw="mx-2" />
              <Link to={`/b/${brand.id}/s/${season.id}/l/${line.id}`} tw="hover:(text-gray-900)">
                {`${line[`name${languageSuffix}`]}`.toUpperCase()}
              </Link>
            </Fragment>
          )}
        </div>

        <div tw="flex text-gray-500 space-x-6 px-6">
          <div>
            <p tw="hover:text-gray-900 p-0 m-0">{i18n.language === "it" ? "ORDINATO A-Z" : "SORT BY A-Z"}</p>
          </div>
          <div>
            <button tw="hover:text-gray-900 focus:outline-none" onClick={() => setCollapsed(!collapsed)}>
              {i18n.language === "it" ? "FILTRI" : "FILTERS"}
              {collapsed && <FontAwesomeIcon tw="ml-2" icon={faChevronDown} />}
              {!collapsed && <FontAwesomeIcon tw="ml-2" icon={faChevronUp} />}
            </button>
          </div>
        </div>
      </div>

      {(filteredColorGroups.length > 0 || filteredCompositionGroups.length > 0 || filteredKindGroups.length > 0) && (
        <div tw="flex w-full justify-between px-6 mt-2">
          <div tw="text-gray-500">
            {i18n.language === "it" ? "FILTRI ATTIVI" : "ACTIVE FILTERS"}
            <FontAwesomeIcon icon={faChevronRight} tw="mx-2" />
            {filteredColorGroups.map((colorGroup: any) => (
              <button
                key={colorGroup.code}
                onClick={() => delColorGroups(colorGroup)}
                tw="border-default p-2 mx-1 text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
              >
                <img
                  tw="w-4 h-4 rounded-full inline mr-2"
                  src={`${appEndpointStore}${colorGroup.patch}`}
                  alt={colorGroup[`name${languageSuffix}`]}
                />
                {colorGroup[`name${languageSuffix}`]}
                <FontAwesomeIcon icon={faTimes} tw="ml-2" />
              </button>
            ))}
            {filteredCompositionGroups.map((compositionGroup: any) => (
              <button
                key={compositionGroup.code}
                onClick={() => delCompositionGroups(compositionGroup)}
                tw="border-default p-2 mx-1 text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
              >
                {compositionGroup[`name${languageSuffix}`]}
                <FontAwesomeIcon icon={faTimes} tw="ml-2" />
              </button>
            ))}
            {filteredKindGroups.map((kindGroup: any) => (
              <button
                key={kindGroup.code}
                onClick={() => delKindGroups(kindGroup)}
                tw="border-default p-2 mx-1 text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
              >
                {kindGroup[`name${languageSuffix}`]}
                <FontAwesomeIcon icon={faTimes} tw="ml-2" />
              </button>
            ))}
          </div>
        </div>
      )}

      {!collapsed && (
        <div tw="border-b-default border-gray-700 py-4">
          <div tw="flex" style={{ justifyContent: "space-evenly" }}>
            {colorGroups.length > 0 && (
              <div tw="flex flex-col space-y-2 items-start">
                <p>{i18n.language === "it" ? "COLORI" : "COLORS"}</p>
                {colorGroups.map((colorGroup: any) => (
                  <button
                    key={colorGroup.code}
                    onClick={() => addColorGroups(colorGroup)}
                    tw="text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
                  >
                    <img
                      tw="w-4 h-4 rounded-full inline mr-2"
                      src={`${appEndpointStore}${colorGroup.patch}`}
                      alt={colorGroup[`name${languageSuffix}`]}
                    />
                    {colorGroup[`name${languageSuffix}`]}
                  </button>
                ))}
              </div>
            )}

            {compositionGroups.length > 0 && (
              <div tw="flex flex-col space-y-2 items-start">
                <p>{i18n.language === "it" ? "COMPOSIZIONI" : "COMPOSITIONS"}</p>
                {compositionGroups.map((compositionGroup: any) => (
                  <button
                    key={compositionGroup.code}
                    onClick={() => addCompositionGroups(compositionGroup)}
                    tw="text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
                  >
                    {compositionGroup[`name${languageSuffix}`]}
                  </button>
                ))}
              </div>
            )}

            {kindGroups.length > 0 && (
              <div tw="flex flex-col space-y-2 items-start">
                <p>{i18n.language === "it" ? "CATEGORIE" : "KINDS"}</p>
                {kindGroups.map((kindGroup: any) => (
                  <button
                    key={kindGroup.code}
                    onClick={() => addKindGroups(kindGroup)}
                    tw="text-gray-500 text-xs focus:outline-none hover:(text-gray-900)"
                  >
                    {kindGroup[`name${languageSuffix}`]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
